import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import LoyaltyRoundedIcon from '@mui/icons-material/LoyaltyRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { useBonusAccount, useCustomerCards, useCustomerTier } from '@/features/loyalty/hooks';
import { useLoyaltyPermissions } from '@/features/loyalty/lib/useLoyaltyPermissions';
import { CustomerConversationSummary } from '@/features/conversations';
import { usePermissions } from '@/shared/permissions';
import { ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import {
  useCustomerAccounts,
  useCustomerAddresses,
  useCustomerConsents,
  useCustomerContacts,
  useCustomerRecipients,
  useCustomerSummary,
} from '../../hooks';
import type { CustomerSummary } from '../../model/types';
import {
  ConsentValueChip,
  CustomerStatusChip,
  DefaultChip,
  PrimaryChip,
  ProfileStatusChip,
  VerificationChip,
} from '../components/chips';
import {
  customerDisplayName,
  detailLabel,
  formatDate,
  formatDateTime,
  fullName,
  humanizeCode,
  initials,
} from '../components/format';
import { KeyValue, ListSection, RecordCard, SectionCard } from '../components/sections';

const BACK_LINK = '/customers';

function avatarColor(status?: string | null): string {
  if (status === 'BLOCKED') {
    return 'error.main';
  }
  if (status === 'DELETED' || status === 'ANONYMIZED') {
    return 'text.disabled';
  }
  if (status === 'REGISTRATION_REQUIRED') {
    return 'warning.main';
  }
  return 'primary.main';
}

function CopyableId({ customerId }: { customerId: string }) {
  const { t } = useTranslation('customers');
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(customerId).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
      >
        {customerId}
      </Typography>
      <Tooltip title={copied ? t('detail.copyId.copied') : t('detail.copyId.copy')}>
        <IconButton size="small" onClick={copy} aria-label={t('detail.copyId.copy')}>
          {copied ? (
            <CheckRoundedIcon sx={{ fontSize: 15 }} color="success" />
          ) : (
            <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function CustomerHeader({
  customer,
  canViewConversations,
}: {
  customer: CustomerSummary;
  canViewConversations: boolean;
}) {
  const { t } = useTranslation('customers');
  const loyalty = useLoyaltyPermissions();
  const name = customerDisplayName(customer);
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.75 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2.5}
        alignItems={{ md: 'flex-start' }}
      >
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: avatarColor(customer.status),
            fontWeight: 700,
            fontSize: 24,
          }}
        >
          {initials(name)}
        </Avatar>

        <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={0.75}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 800, wordBreak: 'break-word' }}
            >
              {name}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <CustomerStatusChip status={customer.status} />
              <ProfileStatusChip status={customer.profileStatus} />
              {customer.registrationChannel ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={humanizeCode(customer.registrationChannel)}
                />
              ) : null}
              {customer.deleted ? (
                <Chip size="small" color="warning" label={t('detail.chips.anonymized')} />
              ) : null}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<PhoneRoundedIcon />}
              component="a"
              href={customer.primaryPhone ? `tel:${customer.primaryPhone}` : undefined}
              disabled={!customer.primaryPhone}
            >
              {customer.primaryPhone ?? t('detail.contact.noPhone')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<EmailRoundedIcon />}
              component="a"
              href={customer.primaryEmail ? `mailto:${customer.primaryEmail}` : undefined}
              disabled={!customer.primaryEmail}
            >
              {customer.primaryEmail ?? t('detail.contact.noEmail')}
            </Button>
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <CopyableId customerId={customer.customerId} />
            <Typography variant="caption" color="text.secondary">
              {t('detail.updated', { date: formatDateTime(customer.updatedAt) })}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {canViewConversations ? (
            <Button
              variant="outlined"
              startIcon={<ForumRoundedIcon />}
              component="a"
              href="#conversation"
            >
              {t('detail.conversation.headerButton')}
            </Button>
          ) : null}
          {loyalty.canView ? (
            <Button
              variant="outlined"
              startIcon={<LoyaltyRoundedIcon />}
              component={RouterLink}
              to={`/loyalty/customers?customerId=${customer.customerId}`}
            >
              {t('detail.loyalty.label')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * The "Conversation" section (design-customer-360-conversation-tab.md §5) — after Accounts, before
 * Loyalty (accounts are the messenger identities; the conversation is what happened on them). Rendered
 * only with `conversations.view`; absent otherwise (no visible-but-refusing card, adr-merchant-portal-017).
 */
function ConversationSection({ customerId }: { customerId: string }) {
  const { t } = useTranslation('customers');
  const { hasPermission } = usePermissions();
  const canView = hasPermission('conversations.view');
  const canManage = canView && hasPermission('conversations.manage');
  const canReply = canManage && hasPermission('conversations.feature.operator-reply');
  const canViewPii = hasPermission('customers.feature.pii-view');

  if (!canView) {
    return null;
  }

  return (
    <Box id="conversation" sx={{ scrollMarginTop: 16 }}>
      <SectionCard
        icon={<ForumRoundedIcon fontSize="small" />}
        title={t('detail.sections.conversation.title')}
      >
        <CustomerConversationSummary
          customerId={customerId}
          messageLimit={8}
          canViewPii={canViewPii}
          canOpenInbox={canView}
          canReply={canReply}
        />
      </SectionCard>
    </Box>
  );
}

function LoyaltySection({ customerId }: { customerId: string }) {
  const { t } = useTranslation('customers');
  const loyalty = useLoyaltyPermissions();
  const bonus = useBonusAccount(customerId, { enabled: loyalty.canView });
  const tier = useCustomerTier(customerId, { enabled: loyalty.canView });
  const cards = useCustomerCards(customerId, { enabled: loyalty.canView });

  if (!loyalty.canView) {
    return null;
  }

  return (
    <SectionCard icon={<LoyaltyRoundedIcon fontSize="small" />} title={t('detail.loyalty.label')}>
      {bonus.isLoading ? (
        <Typography variant="body2" color="text.secondary">
          {t('detail.loyalty.loading')}
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              color="primary"
              label={t('detail.loyalty.points', { count: bonus.data?.balance ?? 0 })}
            />
            {bonus.data?.status ? (
              <Chip variant="outlined" label={humanizeCode(bonus.data.status)} />
            ) : null}
            {tier.data?.name ? <Chip variant="outlined" label={tier.data.name} /> : null}
            <Chip
              variant="outlined"
              label={t('detail.loyalty.cards', { count: cards.data?.length ?? 0 })}
            />
          </Stack>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/loyalty/customers?customerId=${customerId}`}
          >
            {t('detail.loyalty.openWorkspace')}
          </Button>
        </Stack>
      )}
    </SectionCard>
  );
}

export function CustomerDetailPage() {
  const { t } = useTranslation('customers');
  const { customerId = '' } = useParams();
  const { hasPermission } = usePermissions();
  const canView = hasPermission('customers.view');
  const canViewConversations = hasPermission('conversations.view');

  const summary = useCustomerSummary(customerId, { enabled: canView });
  const contacts = useCustomerContacts(customerId, { enabled: canView });
  const consents = useCustomerConsents(customerId, { enabled: canView });
  const addresses = useCustomerAddresses(customerId, { enabled: canView });
  const recipients = useCustomerRecipients(customerId, { enabled: canView });
  const accounts = useCustomerAccounts(customerId, { enabled: canView });

  const backButton = (
    <Button
      component={RouterLink}
      to={BACK_LINK}
      startIcon={<ArrowBackRoundedIcon />}
      color="inherit"
    >
      {t('detail.backButton')}
    </Button>
  );

  if (!canView) {
    return (
      <PageContainer actions={backButton}>
        <ErrorState
          title={t('detail.accessRestricted.title')}
          message={t('detail.accessRestricted.message')}
        />
      </PageContainer>
    );
  }

  if (summary.isLoading) {
    return (
      <PageContainer actions={backButton}>
        <LoadingState label={t('detail.loading')} />
      </PageContainer>
    );
  }

  if (summary.isError || !summary.data) {
    const notFound = summary.error?.status === 404;
    return (
      <PageContainer actions={backButton}>
        <ErrorState
          title={notFound ? t('detail.notFound.title') : t('detail.loadError.title')}
          message={
            notFound
              ? t('detail.notFound.message')
              : (summary.error?.message ?? t('common:error.unknown'))
          }
          onRetry={notFound ? undefined : () => void summary.refetch()}
        />
      </PageContainer>
    );
  }

  const customer = summary.data;

  return (
    <PageContainer maxWidth={1200} actions={backButton}>
      <Stack spacing={2.5}>
        <CustomerHeader customer={customer} canViewConversations={canViewConversations} />

        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'stretch',
          }}
        >
          <SectionCard
            icon={<ContactPhoneRoundedIcon fontSize="small" />}
            title={t('detail.sections.contacts.title')}
            count={contacts.data?.length}
          >
            <ListSection
              query={contacts}
              loadingLabel={t('detail.sections.contacts.loading')}
              emptyTitle={t('detail.sections.contacts.emptyTitle')}
              emptyDescription={t('detail.sections.contacts.emptyDescription')}
              getKey={(contact) => contact.id}
              renderItem={(contact) => (
                <RecordCard
                  title={detailLabel(contact.value)}
                  subtitle={humanizeCode(contact.contactTypeCode)}
                  chips={
                    <>
                      <VerificationChip status={contact.verificationStatus} />
                      {contact.primary ? <PrimaryChip /> : null}
                      {contact.active === false ? (
                        <Chip size="small" label={t('detail.chips.inactive')} />
                      ) : null}
                    </>
                  }
                >
                  {contact.normalizedValue && contact.normalizedValue !== contact.value ? (
                    <KeyValue
                      label={t('detail.sections.contacts.fields.normalized')}
                      value={contact.normalizedValue}
                    />
                  ) : null}
                  {contact.verificationMethod ? (
                    <KeyValue
                      label={t('detail.sections.contacts.fields.method')}
                      value={humanizeCode(contact.verificationMethod)}
                    />
                  ) : null}
                  {contact.verifiedAt ? (
                    <KeyValue
                      label={t('detail.sections.contacts.fields.verified')}
                      value={formatDateTime(contact.verifiedAt)}
                    />
                  ) : null}
                </RecordCard>
              )}
            />
          </SectionCard>

          <SectionCard
            icon={<VerifiedUserRoundedIcon fontSize="small" />}
            title={t('detail.sections.consents.title')}
            count={consents.data?.length}
          >
            <ListSection
              query={consents}
              loadingLabel={t('detail.sections.consents.loading')}
              emptyTitle={t('detail.sections.consents.emptyTitle')}
              emptyDescription={t('detail.sections.consents.emptyDescription')}
              getKey={(consent) => consent.id}
              renderItem={(consent) => (
                <RecordCard
                  title={humanizeCode(consent.usage ?? consent.type)}
                  subtitle={consent.touchpoint ? humanizeCode(consent.touchpoint) : undefined}
                  chips={
                    <>
                      <ConsentValueChip value={consent.value} accepted={consent.accepted} />
                      {consent.legalBasis ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={humanizeCode(consent.legalBasis)}
                        />
                      ) : null}
                    </>
                  }
                >
                  {consent.captureSource ? (
                    <KeyValue
                      label={t('detail.sections.consents.fields.capturedVia')}
                      value={humanizeCode(consent.captureSource)}
                    />
                  ) : null}
                  <KeyValue
                    label={t('detail.sections.consents.fields.date')}
                    value={formatDateTime(consent.captureDate ?? consent.acceptedAt)}
                  />
                  {(consent.documentVersion ?? consent.version) ? (
                    <KeyValue
                      label={t('detail.sections.consents.fields.document')}
                      value={t('detail.sections.consents.fields.documentValue', {
                        version: (consent.documentVersion ?? consent.version) as string,
                      })}
                    />
                  ) : null}
                </RecordCard>
              )}
            />
          </SectionCard>

          <SectionCard
            icon={<PlaceRoundedIcon fontSize="small" />}
            title={t('detail.sections.addresses.title')}
            count={addresses.data?.length}
          >
            <ListSection
              query={addresses}
              loadingLabel={t('detail.sections.addresses.loading')}
              emptyTitle={t('detail.sections.addresses.emptyTitle')}
              emptyDescription={t('detail.sections.addresses.emptyDescription')}
              getKey={(address) => address.id}
              renderItem={(address) => (
                <RecordCard
                  title={detailLabel(address.label ?? address.fullAddress)}
                  subtitle={address.label ? address.fullAddress : undefined}
                  chips={
                    address.active === false ? (
                      <Chip size="small" label={t('detail.chips.inactive')} />
                    ) : undefined
                  }
                >
                  {address.recipientName ? (
                    <KeyValue
                      label={t('detail.sections.addresses.fields.recipient')}
                      value={address.recipientName}
                    />
                  ) : null}
                  {address.recipientPhone ? (
                    <KeyValue
                      label={t('detail.sections.addresses.fields.phone')}
                      value={address.recipientPhone}
                    />
                  ) : null}
                  {address.comment ? (
                    <KeyValue
                      label={t('detail.sections.addresses.fields.note')}
                      value={address.comment}
                    />
                  ) : null}
                </RecordCard>
              )}
            />
          </SectionCard>

          <SectionCard
            icon={<GroupsRoundedIcon fontSize="small" />}
            title={t('detail.sections.recipients.title')}
            count={recipients.data?.length}
          >
            <ListSection
              query={recipients}
              loadingLabel={t('detail.sections.recipients.loading')}
              emptyTitle={t('detail.sections.recipients.emptyTitle')}
              emptyDescription={t('detail.sections.recipients.emptyDescription')}
              getKey={(recipient) => recipient.id}
              renderItem={(recipient) => (
                <RecordCard
                  title={
                    fullName(recipient.firstName, recipient.lastName) ||
                    t('detail.sections.recipients.fallbackName')
                  }
                  subtitle={recipient.phone ?? recipient.email ?? undefined}
                  chips={
                    <>
                      {recipient.isDefault ? <DefaultChip /> : null}
                      {recipient.isFavorite ? (
                        <Chip size="small" variant="outlined" label={t('detail.chips.favorite')} />
                      ) : null}
                    </>
                  }
                >
                  {recipient.email ? (
                    <KeyValue
                      label={t('detail.sections.recipients.fields.email')}
                      value={recipient.email}
                    />
                  ) : null}
                  {recipient.telegramUsername ? (
                    <KeyValue
                      label={t('detail.sections.recipients.fields.telegram')}
                      value={`@${recipient.telegramUsername.replace(/^@/, '')}`}
                    />
                  ) : null}
                  {recipient.comment ? (
                    <KeyValue
                      label={t('detail.sections.recipients.fields.note')}
                      value={recipient.comment}
                    />
                  ) : null}
                </RecordCard>
              )}
            />
          </SectionCard>

          <SectionCard
            icon={<HubRoundedIcon fontSize="small" />}
            title={t('detail.sections.accounts.title')}
            count={accounts.data?.length}
          >
            <ListSection
              query={accounts}
              loadingLabel={t('detail.sections.accounts.loading')}
              emptyTitle={t('detail.sections.accounts.emptyTitle')}
              emptyDescription={t('detail.sections.accounts.emptyDescription')}
              getKey={(account, index) => `${account.provider}-${account.providerUserId ?? index}`}
              renderItem={(account) => (
                <RecordCard
                  title={humanizeCode(account.provider)}
                  subtitle={
                    account.username
                      ? `@${account.username.replace(/^@/, '')}`
                      : (account.providerUserId ?? undefined)
                  }
                  chips={
                    <>
                      {account.channel ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={humanizeCode(account.channel)}
                        />
                      ) : null}
                      {account.active === false ? (
                        <Chip size="small" label={t('detail.chips.inactive')} />
                      ) : null}
                    </>
                  }
                >
                  {account.providerUserId ? (
                    <KeyValue
                      label={t('detail.sections.accounts.fields.providerId')}
                      value={account.providerUserId}
                    />
                  ) : null}
                  {account.createdAt ? (
                    <KeyValue
                      label={t('detail.sections.accounts.fields.linked')}
                      value={formatDate(account.createdAt)}
                    />
                  ) : null}
                </RecordCard>
              )}
            />
          </SectionCard>

          <ConversationSection customerId={customer.customerId} />
          <LoyaltySection customerId={customer.customerId} />
        </Box>
      </Stack>
    </PageContainer>
  );
}
